import { FloatingNode, FloatingTree, useFloatingNodeId, useFloatingParentNodeId } from '@floating-ui/react'

import type { PopoverProps } from './popover.types'

import { filterUndefined, runIfFn } from '../utils'
import { PopoverProvider } from './context'
import PopoverContent from './popoverContent'
import PopoverTrigger from './popoverTrigger'
import usePopover from './usePopover'

/**
 * Uses 'usePopover' hook to expose relevant data to its children parts.
 * By default, renders provided content only when popover is opened (lazy).
 */
function PopoverInner(props: PopoverProps) {
  const nodeId = useFloatingNodeId()
  const { children, isLazy = true, ...rest } = props
  const popoverProps = usePopover({ ...rest, nodeId })
  const { close, isOpen, open } = popoverProps
  const context = filterUndefined({ isLazy, ...popoverProps })

  return (
    <FloatingNode id={nodeId}>
      <PopoverProvider value={context}>{runIfFn(children, { close, isOpen, open })}</PopoverProvider>
    </FloatingNode>
  )
}

export default function Popover(props: PopoverProps) {
  const parentId = useFloatingParentNodeId()

  if (parentId === null) {
    return (
      <FloatingTree>
        <PopoverInner {...props} />
      </FloatingTree>
    )
  }

  return <PopoverInner {...props} />
}

Popover.Content = PopoverContent
Popover.Trigger = PopoverTrigger
Popover.displayName = 'Popover'
